/**
 * OpenChoresTile — "Oppgavebunken" tile on the idle dashboard.
 *
 * Plan 01-14 (day-one placeholder, count=0/leader=null) → Plan 02-17
 * upgrade: hook into the live pool board feed (usePoolBoard) and the
 * weekly leaderboard (useWeeklyLeaderboard) so the tile shows real
 * household activity. Subline now reads "{nick} — {points} p" with a
 * fallback "Ingen poeng enda denne uka" until someone scores.
 *
 * Backwards-compat surface: the `count` and `leader` props remain as
 * test-time and SSR-time overrides. When set, they win over the live
 * query data — this keeps Plan 01-14's EmptyTiles tests green and lets
 * future surfaces (settings preview, README screenshot) render the tile
 * with a fixed shape.
 *
 * Stell_Kjokken (#160):
 *   - Stort tall: ledige kort (status `available`) + «ledige», 48 px.
 *     Tidligere talte flisen alle kort på tavla; med ordet «ledige» må
 *     tallet bare telle de ledige.
 *   - Tom: "Oppgavebunken er tom — bra jobba!" når ingen er ledige
 *   - Ukas leder: avatar + "{{nick}} — {{points}} p" | tom-tekst. Søylene per
 *     barn fra skissen er utelatt: toppliste krever innlogging, og
 *     kjøkkenskjermen hviler ofte uten økt (PLAN.md steg 10a).
 *   - Tap navigates to /oppgavebunken (Plan 09 route)
 *
 * Tap behaviour: the entire card is a button — pointer-down on idle
 * dashboard wakes the tablet (DASH-03), so the tile's onClick is
 * primarily for the interactive-mode use case where the dashboard is
 * already awake.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { GLASS_SURFACE } from '../../../components/primitives/Card';
import { InitialAvatar, asColorSlot } from '../../../components/primitives/InitialAvatar';
import { formatPoints } from '../../../i18n/formatters';
import { usePoolBoard } from '../../chores/useChoresQuery';
import { useWeeklyLeaderboard } from '../../leaderboard/useLeaderboardQueries';

export type ChoreLeader = {
  name: string;
  points: number;
  /** Personfargen (members.color_token); uten den får avataren standardfargen. */
  slot?: string;
};

interface OpenChoresTileProps {
  /** Override pool count (Phase 1 prop kept for tests + SSR previews). */
  count?: number;
  /** Override leader subline (Phase 1 prop kept for tests + SSR previews). */
  leader?: ChoreLeader | null;
}

export function OpenChoresTile({
  count: countOverride,
  leader: leaderOverride,
}: OpenChoresTileProps = {}): ReactNode {
  const { t } = useTranslation();
  const nav = useNavigate();

  // Live data — TanStack Query. Underlying fetches share a single client
  // (DashboardQueryProvider) so identical queries dedupe across tiles.
  const poolQuery = usePoolBoard();
  const leaderQuery = useWeeklyLeaderboard();

  // Prop overrides win; otherwise fall back to live data; otherwise 0/null.
  // «ledige» = kort som kan tas, samme telling som chipen på Oppgavebunken.
  // Uten tavle (laster, feil, eller ingen økt på kjøkkenet) vet vi ikke
  // antallet: da står «–», ikke «0 … tom — bra jobba!» (#160).
  const board = Array.isArray(poolQuery.data) ? poolQuery.data : null;
  const count =
    typeof countOverride === 'number'
      ? countOverride
      : board
        ? board.filter((c) => c.status === 'available').length
        : null;

  // Leader: top of the weekly leaderboard if any positive earner exists.
  // The leaderboard.service ranks by points DESC — rows[0] is the leader.
  // Skip empty leaderboard (zero earners) — the API may return rows with
  // points=0 for everyone, in which case the empty-fallback line shows.
  const top = leaderQuery.data?.[0];
  const liveLeader: ChoreLeader | null =
    top && top.points > 0
      ? { name: top.short_name, points: top.points, slot: top.slot }
      : null;
  const leader =
    leaderOverride !== undefined ? leaderOverride : liveLeader;

  return (
    <button
      type="button"
      data-testid="open-chores-tile"
      onClick={() => nav('/oppgavebunken')}
      className={`${GLASS_SURFACE} flex h-full w-full cursor-pointer flex-col gap-3.5 rounded-xl p-6 text-left`}
    >
      <span data-testid="chores-title" className="text-heading-l font-extrabold">
        {t('dashboard.chores.title')}
      </span>
      <span className="flex items-baseline gap-2">
        <span
          data-testid="chores-count"
          className="text-5xl tnum text-ink leading-none font-extrabold tracking-tight"
        >
          {count ?? '–'}
        </span>
        <span className="text-xl font-bold">{t('dashboard.chores.available')}</span>
      </span>
      {count === 0 ? (
        <span data-testid="chores-empty" className="text-ink-2 text-body">
          {t('dashboard.chores.empty')}
        </span>
      ) : null}
      <span className="mt-auto flex flex-col gap-2">
        <span className="text-ink-2 text-body font-bold">{t('home.leader.label')}</span>
        <span className="flex items-center gap-3">
          {leader ? (
            <InitialAvatar name={leader.name} colorSlot={asColorSlot(leader.slot)} size={40} decorative />
          ) : null}
          <span data-testid="chores-leader" className="text-body font-extrabold">
            {leader
              ? t('dashboard.openChores.subline.leader', {
                  nick: leader.name,
                  points: formatPoints(leader.points),
                })
              : t('dashboard.openChores.subline.empty')}
          </span>
        </span>
      </span>
    </button>
  );
}
